import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Phone, Lock, AlertCircle, Eye, EyeOff, Building2 } from 'lucide-react'
import { useAuthStore } from '@/stores/authStore'
import { authApi } from '@/api'
import type { TenantOption } from '@/types'

export default function LoginPage() {
  const navigate = useNavigate()
  const { setAuth } = useAuthStore()
  const [phone, setPhone] = useState('')
  const [password, setPassword] = useState('')
  const [error, setError] = useState('')
  const [loading, setLoading] = useState(false)
  const [creatingTenant, setCreatingTenant] = useState(false)
  const [showPassword, setShowPassword] = useState(false)

  // 多車行選擇狀態
  const [multiTenants, setMultiTenants] = useState<TenantOption[] | null>(null)
  const [sessionToken, setSessionToken] = useState('')
  const [selectingTenant, setSelectingTenant] = useState(false)

  const applyLoginResponse = (response: any) => {
    setAuth(response.user!, response.access_token!)
    localStorage.setItem('refresh_token', response.refresh_token!)
    if (response.tenant) {
      localStorage.setItem('tenant', JSON.stringify(response.tenant))
    }
    navigate('/dashboard')
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError('')
    setLoading(true)

    try {
      const response = await authApi.login({ phone, password })

      if (response.type === 'multi_tenant') {
        // 多車行：顯示選擇畫面
        setSessionToken(response.session_token!)
        setMultiTenants(response.tenants!)
      } else {
        applyLoginResponse(response)
      }
    } catch (err: unknown) {
      if (err && typeof err === 'object' && 'response' in err) {
        const responseErr = err as { response?: { data?: { detail?: string } } }
        setError(responseErr.response?.data?.detail || '登入失敗，請檢查您的帳號密碼')
      } else {
        setError('登入失敗，請稍後再試')
      }
    } finally {
      setLoading(false)
    }
  }

  const handleSelectTenant = async (tenantId: string) => {
    setError('')
    setSelectingTenant(true)
    try {
      const response = await authApi.selectTenant(sessionToken, tenantId)
      applyLoginResponse(response)
    } catch (err: unknown) {
      if (err && typeof err === 'object' && 'response' in err) {
        const responseErr = err as { response?: { data?: { detail?: string } } }
        setError(responseErr.response?.data?.detail || '選擇車行失敗，請重新登入')
      } else {
        setError('選擇車行失敗，請稍後再試')
      }
      setMultiTenants(null)
      setSessionToken('')
    } finally {
      setSelectingTenant(false)
    }
  }

  const handleTenantLogin = async () => {
    setError('')
    setLoading(true)

    try {
      const response = await authApi.login({
        phone: '0912-345-678',
        password: 'admin@123',
      })
      setAuth(response.user, response.access_token)
      localStorage.setItem('refresh_token', response.refresh_token)
      
      if (response.tenant) {
        localStorage.setItem('tenant', JSON.stringify(response.tenant))
      }
      
      navigate('/dashboard')
    } catch (err: unknown) {
      if (err && typeof err === 'object' && 'response' in err) {
        const responseErr = err as { response?: { data?: { error?: string } } }
        setError(responseErr.response?.data?.error || '快速登入失敗，請檢查帳號')
      } else {
        setError('快速登入失敗，請稍後再試')
      }
    } finally {
      setLoading(false)
    }
  }

  const handleDriverLogin = async () => {
    setError('')
    setLoading(true)

    try {
      const response = await authApi.login({
        phone: '0922-222-222',
        password: '12345678',
      })
      setAuth(response.user, response.access_token)
      localStorage.setItem('refresh_token', response.refresh_token)
      
      if (response.tenant) {
        localStorage.setItem('tenant', JSON.stringify(response.tenant))
      }
      
      navigate('/dashboard')
    } catch (err: unknown) {
      if (err && typeof err === 'object' && 'response' in err) {
        const responseErr = err as { response?: { data?: { error?: string } } }
        setError(responseErr.response?.data?.error || '司機登入失敗，請檢查帳號')
      } else {
        setError('司機登入失敗，請稍後再試')
      }
    } finally {
      setLoading(false)
    }
  }

  // 車行選擇畫面
  if (multiTenants) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-gray-50 px-4 dark:bg-gray-900">
        <div className="w-full max-w-md">
          <div className="mb-8 text-center">
            <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-2xl bg-blue-600 shadow-lg shadow-blue-600/30 overflow-hidden">
              <img src="/logo.png" alt="Logo" className="h-full w-full object-contain" />
            </div>
            <h1 className="text-3xl font-bold text-gray-900 dark:text-white">租賃車派遣管理系統</h1>
          </div>
          <div className="rounded-2xl border border-gray-200 bg-white p-8 shadow-xl dark:border-gray-700 dark:bg-gray-800">
            <h2 className="mb-2 text-xl font-semibold text-gray-900 dark:text-white">請選擇車行</h2>
            <p className="mb-6 text-sm text-gray-500 dark:text-gray-400">您的帳號屬於多個車行，請選擇要進入的車行</p>
            {error && (
              <div className="mb-4 flex items-start gap-3 rounded-lg bg-red-50 p-4 text-sm text-red-800 dark:bg-red-900/20 dark:text-red-400">
                <AlertCircle className="mt-0.5 h-5 w-5 shrink-0" />
                <p>{error}</p>
              </div>
            )}
            <div className="space-y-3">
              {multiTenants.map(t => (
                <button
                  key={t.id}
                  disabled={selectingTenant}
                  onClick={() => handleSelectTenant(t.id)}
                  className="w-full flex items-center gap-4 rounded-xl border-2 border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-700 p-4 text-left hover:border-blue-500 hover:bg-blue-50 dark:hover:bg-blue-900/20 transition-colors cursor-pointer disabled:opacity-50"
                >
                  <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-blue-100 dark:bg-blue-900/30">
                    <Building2 className="h-5 w-5 text-blue-600 dark:text-blue-400" />
                  </div>
                  <div>
                    <div className="font-semibold text-gray-900 dark:text-white">{t.name}</div>
                    {t.short_name && <div className="text-sm text-gray-500 dark:text-gray-400">{t.short_name}</div>}
                  </div>
                </button>
              ))}
            </div>
            <button
              onClick={() => { setMultiTenants(null); setSessionToken(''); setError('') }}
              className="mt-4 w-full text-sm text-gray-500 hover:text-gray-700 dark:hover:text-gray-300 cursor-pointer"
            >
              返回登入
            </button>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-gray-50 px-4 dark:bg-gray-900">
      <div className="w-full max-w-md">
        {/* Logo and title */}
        <div className="mb-8 text-center">
          <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-2xl bg-blue-600 shadow-lg shadow-blue-600/30 overflow-hidden">
            <img src="/logo.png" alt="Logo" className="h-full w-full object-contain" />
          </div>
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white">
            租賃車派遣管理系統
          </h1>
        </div>

        {/* Login form */}
        <div className="rounded-2xl border border-gray-200 bg-white p-8 shadow-xl dark:border-gray-700 dark:bg-gray-800">
          <h2 className="mb-6 text-xl font-semibold text-gray-900 dark:text-white">
            登入系統
          </h2>

          {error && (
            <div className="mb-6 flex items-start gap-3 rounded-lg bg-red-50 p-4 text-sm text-red-800 dark:bg-red-900/20 dark:text-red-400">
              <AlertCircle className="mt-0.5 h-5 w-5 shrink-0" />
              <p>{error}</p>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-5">
            {/* Phone */}
            <div>
              <label
                htmlFor="phone"
                className="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300"
              >
                手機號碼 <span className="text-xs text-gray-500">（可帶或不帶"-"）</span>
              </label>
              <div className="relative">
                <Phone className="absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 text-gray-400" />
                <input
                  id="phone"
                  type="tel"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  placeholder="0900000000 或 0900-000-000"
                  required
                  className="w-full rounded-lg border border-gray-300 bg-gray-50 py-2.5 pl-10 pr-4 text-gray-900 placeholder-gray-500 focus:border-blue-500 focus:bg-white focus:outline-none focus:ring-2 focus:ring-blue-500/20 dark:border-gray-600 dark:bg-gray-700 dark:text-white dark:placeholder-gray-400"
                />
              </div>
            </div>

            {/* Password */}
            <div>
              <label
                htmlFor="password"
                className="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300"
              >
                密碼
              </label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 text-gray-400" />
                <input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  required
                  className="w-full rounded-lg border border-gray-300 bg-gray-50 py-2.5 pl-10 pr-10 text-gray-900 placeholder-gray-500 focus:border-blue-500 focus:bg-white focus:outline-none focus:ring-2 focus:ring-blue-500/20 dark:border-gray-600 dark:bg-gray-700 dark:text-white dark:placeholder-gray-400"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 cursor-pointer"
                >
                  {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                </button>
              </div>
            </div>

            {/* Remember me & Forgot password */}
            <div className="flex items-center justify-between text-sm">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                />
                <span className="text-gray-600 dark:text-gray-400">記住我</span>
              </label>
              <a
                href="#"
                className="font-medium text-blue-600 hover:text-blue-700 dark:text-blue-400"
              >
                忘記密碼？
              </a>
            </div>

            {/* Submit button */}
            <button
              type="submit"
              disabled={loading}
              className="w-full rounded-lg bg-blue-600 py-2.5 font-semibold text-white hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 dark:bg-blue-600 dark:hover:bg-blue-700 transition-colors duration-150"
            >
              {loading ? '登入中...' : '登入'}
            </button>
          </form>

        </div>

        {/* Footer */}
        <p className="mt-6 text-center text-sm text-gray-500 dark:text-gray-400">
          © 2024 租賃車派遣管理系統. All rights reserved.
        </p>
      </div>
    </div>
  )
}
